/**
 * 9MM ENERGY DRINK — 3D Animation Engine
 * Handles: Scroll reveals, 3D tilt, parallax, particles, header scroll
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.innerWidth <= 768 || ('ontouchstart' in window);

  /* =============================================
     1. SCROLL REVEAL (Intersection Observer)
     ============================================= */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('.nm-reveal, .nm-reveal-scale');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    reveals.forEach((el) => observer.observe(el));
  }

  /* =============================================
     2. 3D TILT EFFECT (Mouse-tracking on cards)
     ============================================= */
  function initTiltEffect() {
    if (prefersReducedMotion || isMobile) return;

    const tiltElements = document.querySelectorAll('.nm-tilt');
    tiltElements.forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = ((y - centerY) / centerY) * -8;
        const rotateY = ((x - centerX) / centerX) * 8;

        const inner = el.querySelector('.nm-tilt-inner') || el;
        inner.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      });

      el.addEventListener('mouseleave', () => {
        const inner = el.querySelector('.nm-tilt-inner') || el;
        inner.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      });
    });
  }

  /* =============================================
     3. 3D CAN INTERACTIVE ROTATION
     ============================================= */
  function initCanInteraction() {
    if (prefersReducedMotion || isMobile) return;

    const canWrappers = document.querySelectorAll('.nm-can-wrapper');
    canWrappers.forEach((wrapper) => {
      const scene = wrapper.closest('.nm-can-scene');
      if (!scene) return;

      scene.addEventListener('mousemove', (e) => {
        const rect = scene.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        wrapper.style.transform = `
          translateY(${-15 + y * -10}px)
          rotateY(${x * 25}deg)
          rotateX(${y * -15}deg)
        `;
      });

      scene.addEventListener('mouseleave', () => {
        wrapper.style.transform = '';
      });
    });
  }

  /* =============================================
     4. PARALLAX GLOW ORBS (Mouse-tracking)
     ============================================= */
  function initParallaxGlow() {
    if (prefersReducedMotion || isMobile) return;

    const glows = document.querySelectorAll('.nm-hero-glow');
    if (!glows.length) return;

    let ticking = false;

    document.addEventListener('mousemove', (e) => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 40;
        const y = (e.clientY / window.innerHeight - 0.5) * 40;

        glows.forEach((glow, i) => {
          const factor = i === 0 ? 1 : -0.7;
          glow.style.transform = `translate(${x * factor}px, ${y * factor}px)`;
        });

        ticking = false;
      });
    });
  }

  /* =============================================
     5. HEADER SCROLL STATE
     ============================================= */
  function initHeaderScroll() {
    const header = document.querySelector('.nm-header');
    const wrap = document.querySelector('.nm-sticky-header-wrap');
    if (!header) return;

    const onScroll = () => {
      const scrollY = window.scrollY;
      const scrolled = scrollY > 50;
      header.classList.toggle('is-scrolled', scrolled);
      if (wrap) wrap.classList.toggle('is-scrolled', scrolled);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* =============================================
     6. MOBILE MENU TOGGLE
     ============================================= */
  function initMobileMenu() {
    var toggle = document.querySelector('.nm-menu-toggle');
    var nav    = document.querySelector('.nm-nav');
    var wrap   = document.querySelector('.nm-sticky-header-wrap');
    if (!toggle || !nav || !wrap) return;

    // Bloquer touchmove sur le fond (iOS Safari ignore overflow:hidden sur body)
    function blockScroll(e) {
      // Autoriser le scroll à l'intérieur de la nav
      if (nav.contains(e.target)) return;
      e.preventDefault();
    }

    function openMenu() {
      // Calculer la hauteur réelle du wrapper (barre annonce + header)
      var wrapH = wrap.offsetHeight;
      nav.style.top    = wrapH + 'px';
      nav.style.height = 'calc(100dvh - ' + wrapH + 'px)';
      wrap.classList.add('is-menu-open');
      document.body.classList.add('nm-menu-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('touchmove', blockScroll, { passive: false });
    }

    function closeMenu() {
      wrap.classList.remove('is-menu-open');
      document.body.classList.remove('nm-menu-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('touchmove', blockScroll);
    }

    toggle.addEventListener('click', function() {
      wrap.classList.contains('is-menu-open') ? closeMenu() : openMenu();
    });

    nav.querySelectorAll('.nm-nav-link').forEach(function(link) {
      link.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeMenu();
    });
  }

  /* =============================================
     7. PARTICLE GENERATION
     ============================================= */
  function initParticles() {
    if (prefersReducedMotion || isMobile) return;

    const containers = document.querySelectorAll('.nm-particles');
    containers.forEach((container) => {
      const count = 20;
      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.classList.add('nm-particle');
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.animationDuration = `${8 + Math.random() * 12}s`;
        particle.style.animationDelay = `${Math.random() * 10}s`;
        particle.style.width = `${1 + Math.random() * 2}px`;
        particle.style.height = particle.style.width;
        container.appendChild(particle);
      }
    });
  }

  /* =============================================
     8. SMOOTH SCROLL FOR ANCHOR LINKS
     ============================================= */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const target = document.querySelector(anchor.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* =============================================
     9. LOADING SCREEN
     ============================================= */
  function initLoader() {
    const loader = document.querySelector('.nm-loader');
    if (!loader) return;

    window.addEventListener('load', () => {
      setTimeout(() => {
        loader.classList.add('is-hidden');
      }, 800);
    });
  }

  /* =============================================
     10. COUNTER ANIMATION (Stats)
     ============================================= */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const target = parseInt(el.getAttribute('data-count'), 10);
          const suffix = el.getAttribute('data-suffix') || '';
          const duration = 2000;
          const start = performance.now();

          function update(now) {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.round(target * eased) + suffix;
            if (progress < 1) requestAnimationFrame(update);
          }

          requestAnimationFrame(update);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* =============================================
     11. SPLIT PANEL HOVER EXPANSION
     ============================================= */
  function initSplitHover() {
    if (window.innerWidth < 769) return;

    const panels = document.querySelectorAll('.nm-split-panel');
    const split = document.querySelector('.nm-split');
    if (!split || panels.length < 2) return;

    panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => {
        split.style.gridTemplateColumns = panel.classList.contains('nm-split-panel--body')
          ? '1.3fr 0.7fr'
          : '0.7fr 1.3fr';
      });

      panel.addEventListener('mouseleave', () => {
        split.style.gridTemplateColumns = '1fr 1fr';
      });
    });
  }

  /* =============================================
     INIT ALL
     ============================================= */
  function init() {
    initLoader();
    initScrollReveal();
    initTiltEffect();
    initCanInteraction();
    initParallaxGlow();
    initHeaderScroll();
    initMobileMenu();
    initParticles();
    initSmoothScroll();
    initCounters();
    initSplitHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
